import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react'

// ── Types ──────────────────────────────────────────────────────────────────────

/**
 * Snapshot of a room handshake that Matchmaking.tsx consumes to open its
 * WebSocket via `useMatchContext().connectToRoom(...)`.
 *
 * W2.2: `code` is nullable because the joiner does NOT learn the creator's
 * code — they typed it in already and don't need the banner. Custom-room
 * metadata (roomKind / ownerUserId / targetSize) is also populated here so
 * the Matchmaking lobby UI can branch (queue countdown vs custom Start)
 * without waiting for the first server `lobby` frame.
 */
export interface RoomCreatedState {
  matchId: string
  /** Crockford code for the creator (drives RoomCodeDisplay banner). Null for joiner. */
  code: string | null
  wsUrl: string
  /** W2.2: 'custom' for friend rooms; null when unknown. */
  roomKind?: 'custom' | 'queue' | null
  /** W2.2: owner of the friend room; null when caller is the owner-unknown joiner. */
  ownerUserId?: number | null
  /** W2.2: target player count for the friend room (default 2). */
  targetSize?: number | null
}

// ── Context ────────────────────────────────────────────────────────────────────

export interface LiveMatchSheetContextValue {
  isOpen: boolean
  open: () => void
  close: () => void
  /** Populated after a successful createRoom() call. Cleared on close(). */
  roomCreated: RoomCreatedState | null
  setRoomCreated: (room: RoomCreatedState) => void
  clearRoomCreated: () => void
}

export const LiveMatchSheetContext =
  createContext<LiveMatchSheetContextValue | null>(null)

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Access the global LiveMatchSheet open/close controls.
 *
 * Must be called inside a component tree that has <LiveMatchSheet /> mounted
 * (App.tsx root). Lobby "Live Match" CTA (commit 9) calls `open()` here.
 */
export function useLiveMatchSheet(): LiveMatchSheetContextValue {
  const ctx = useContext(LiveMatchSheetContext)
  if (ctx === null) {
    throw new Error(
      'useLiveMatchSheet must be used within a component tree that mounts <LiveMatchSheet />',
    )
  }
  return ctx
}

// ── State factory (used internally by LiveMatchSheetProvider) ────────────────

export function useLiveMatchSheetState(): LiveMatchSheetContextValue {
  const [isOpen, setIsOpen] = useState(false)
  const [roomCreated, setRoomCreatedState] = useState<RoomCreatedState | null>(null)

  const clearRoomCreated = useCallback((): void => {
    setRoomCreatedState(null)
  }, [])

  const open = useCallback((): void => {
    setIsOpen(true)
  }, [])

  // W2.2 hotfix (F1): close() previously ran `clearRoomCreated()` which
  // wiped the room handshake before Matchmaking.tsx could read it on
  // navigation. The auto-clear is now caller-driven — Matchmaking calls
  // `clearRoomCreated()` once it has successfully opened the WS and seen
  // the first server frame. This is the root cause of "creator must 방
  // 입장 따로 누름" (B1+B2).
  const close = useCallback((): void => {
    setIsOpen(false)
  }, [])

  const setRoomCreated = useCallback((room: RoomCreatedState): void => {
    setRoomCreatedState(room)
  }, [])

  return { isOpen, open, close, roomCreated, setRoomCreated, clearRoomCreated }
}

// ── Provider — must wrap the consumer subtree (Lobby, routes) ────────────────

/**
 * Wrap the part of the tree that needs to call useLiveMatchSheet().
 * The drawer body itself (<LiveMatchSheet />) is rendered as a sibling INSIDE
 * this provider so both producer (body) and consumers (Lobby Live Match CTA)
 * share the same context instance.
 */
export function LiveMatchSheetProvider({
  children,
}: {
  children: ReactNode
}): ReactElement {
  const ctx = useLiveMatchSheetState()
  return (
    <LiveMatchSheetContext.Provider value={ctx}>
      {children}
    </LiveMatchSheetContext.Provider>
  )
}
